import shellCss from './widget-shell.css?raw';

/** Tema padrão (alinhado ao widget legado). */
export const DEFAULT_THEME = {
    backgroundColor: '#213F9A',
    textColor: '#fff',
    accentColor: '#FD4900',
    inputBackgroundColor: '#fff',
    fonts: "system-ui, -apple-system, 'Segoe UI', sans-serif",
} as const;

export type WidgetTheme = {
    backgroundColor?: string;
    textColor?: string;
    accentColor?: string;
    inputBackgroundColor?: string;
    fonts?: string;
    code?: string;
};

/** CSS extra: formulário de cartão mock (substitui Pagar.me no modo demo). */
const CARD_MOCK_CSS = `
  .widget .decola-card-mock {
    margin-top: 0.5rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(255, 255, 255, 0.22);
  }
  .widget .decola-card-mock .decola-card-fields { margin-top: 0; }
  .widget .decola-card-mock .decola-card-row {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.35rem;
    margin-bottom: 0.85rem;
  }
  .widget .decola-card-mock .decola-card-row:last-child { margin-bottom: 0; }
  .widget .decola-card-mock .decola-card-row label {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    margin: 0;
    color: rgba(255, 255, 255, 0.92);
    font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
  }
  .widget .decola-card-mock .decola-card-row input {
    width: 100%;
    max-width: 100%;
    padding: 0.65rem 0.85rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.28);
    font-size: 0.9rem;
    color: #1e293b;
    background: #fff;
    box-sizing: border-box;
    font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
  }
  .widget .decola-card-mock .decola-card-row input:focus {
    outline: none;
    border-color: #fd4900;
    box-shadow: 0 0 0 2px rgba(253, 73, 0, 0.35);
  }
  .widget .decola-card-mock .decola-card-expiry-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0.5rem;
    margin-bottom: 0;
  }
  @media (max-width: 360px) {
    .widget .decola-card-mock .decola-card-expiry-grid {
      grid-template-columns: 1fr;
    }
  }
  .widget .decola-card-mock .decola-card-expiry-grid .decola-card-row { margin-bottom: 0; }
`;

export function getThemeVarsCss(theme: WidgetTheme | null): string {
    const t = theme || {};
    return `
  .widget {
    --widget-bg: ${t.backgroundColor ?? DEFAULT_THEME.backgroundColor};
    --widget-color: ${t.textColor ?? DEFAULT_THEME.textColor};
    --widget-accent: ${t.accentColor ?? DEFAULT_THEME.accentColor};
    --widget-input-bg: ${t.inputBackgroundColor ?? DEFAULT_THEME.inputBackgroundColor};
    --widget-font: ${String(t.fonts ?? DEFAULT_THEME.fonts).replace(/"/g, "'")};
  }
  `;
}

const PROGRESS_BAR_CSS = `
  /* Progress bar */
  .widget-progress-bar { display: flex; gap: 4px; margin-bottom: 0.7rem; }
  .progress-bar-segment { flex: 1; height: 4px; border-radius: 2px; }
  .progress-bar-done { background: #3b82f6; }
  .progress-bar-active { background: #ef4444; }
  .progress-bar-pending { background: rgba(255,255,255,0.25); }

  /* Step subtitle */
  .widget-step-subtitle { font-size: 0.88rem; font-weight: 600; margin: -0.25rem 0 0.75rem; opacity: 0.9; }
`;

const E1_CSS = `
  /* E1 — nome social link */
  .nome-social-link-wrap { text-align: right; margin-top: -0.25rem; margin-bottom: 0.5rem; }
  .link-btn { background: none; border: none; color: var(--widget-accent, #f04e23); font-size: 0.8rem; cursor: pointer; padding: 0; text-decoration: underline; }
  .field-optional { font-weight: normal; opacity: 0.7; font-size: 0.8rem; }

  /* E1 — label + checkbox inline */
  .field-label-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.3rem; }
  .check-inline { display: flex; align-items: center; gap: 0.35rem; font-size: 0.8rem; font-weight: normal; cursor: pointer; }
  .check-inline input[type="checkbox"] { width: 14px; height: 14px; cursor: pointer; }

  /* E1 — CPF input wrap com badge */
  .cpf-input-wrap { position: relative; }
  .cpf-input-wrap input { padding-right: 7.5rem; }
  .cpf-alumni-badge {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.75rem;
    color: #15803d;
    font-weight: 600;
    white-space: nowrap;
    pointer-events: none;
  }
  .cpf-alumni-message { font-size: 0.78rem; color: #ffffff; opacity: 0.9; margin-top: 0.4rem; text-align: center; font-weight: 600; }
  .cpf-enrolled-warning { font-size: 0.82rem; color: #fbbf24; margin-top: 0.3rem; font-weight: 500; }

  /* E1 — tela "já matriculado" */
  .already-enrolled-screen { text-align: center; padding: 1rem 0; }
  .already-enrolled-icon { width: 48px; height: 48px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.6); display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem; font-size: 1.5rem; }
  .already-enrolled-title { font-weight: 700; font-size: 1rem; margin-bottom: 0.75rem; }
  .already-enrolled-body { font-size: 0.85rem; opacity: 0.85; margin-bottom: 0.5rem; line-height: 1.5; }
  .already-enrolled-custom-body { font-size: 0.9rem; line-height: 1.5; text-align: left; }
  .already-enrolled-custom-body p { margin: 0 0 0.6rem; }
  .already-enrolled-custom-body a { color: var(--widget-accent, #FD4900); text-decoration: underline; word-break: break-word; }
  .already-enrolled-custom-body ul,
  .already-enrolled-custom-body ol { margin: 0 0 0.6rem; padding-left: 1.25rem; }
  .already-enrolled-custom-body li { margin-bottom: 0.25rem; }
  .already-enrolled-custom-body strong { font-weight: 700; }
  .already-enrolled-custom-body em { font-style: italic; }

  /* Bloco de contato HTML em payment-error-screen (Formulários → Widget → "Contato para erros de pagamento"). */
  .payment-error-contact { margin: 0.9rem 0 0; font-size: 0.88rem; line-height: 1.5; text-align: left; }
  .payment-error-contact p { margin: 0 0 0.5rem; }
  .payment-error-contact a { color: var(--widget-accent, #FD4900); text-decoration: underline; word-break: break-word; }
  .payment-error-contact ul,
  .payment-error-contact ol { margin: 0 0 0.5rem; padding-left: 1.25rem; }
  .payment-error-contact li { margin-bottom: 0.2rem; }
  .payment-error-contact strong { font-weight: 700; }
  .payment-error-contact em { font-style: italic; }
`;

const E3_CSS = `
  /* E3 — payment method cards */
  .payment-method-card { border: 2px solid rgba(255,255,255,0.2); border-radius: 8px; margin-bottom: 0.35rem; cursor: pointer; overflow: hidden; }
  .payment-method-card.selected { background: var(--widget-accent, #FD4900); border-color: var(--widget-accent, #FD4900); }
  .payment-method-header { display: flex; align-items: center; gap: 0.6rem; padding: 0.45rem 0.85rem; }
  .payment-method-radio { width: 16px; height: 16px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.6); flex-shrink: 0; }
  .payment-method-radio.checked { background: var(--widget-bg, #213F9A); border-color: var(--widget-bg, #213F9A); }
  .payment-method-text { flex: 1; }
  .payment-method-label { display: block; font-weight: 700; font-size: 0.9rem; line-height: 1.2; }
  .payment-method-subtitle { display: block; font-size: 0.72rem; opacity: 0.8; line-height: 1.2; margin-top: 0.05rem; }
  .payment-method-chevron { font-size: 0.8rem; opacity: 0.8; }

  /* E3 — installment radio options */
  .installment-options { display: grid; grid-template-columns: 1fr 1fr; gap: 0.4rem; padding: 0.6rem 1rem 0.75rem; background: rgba(0,0,0,0.18); }
  .installment-radio { display: flex; align-items: center; gap: 0.5rem; font-size: 0.82rem; cursor: pointer; padding: 0.3rem 0.25rem; }
  .installment-radio input[type="radio"] { display: none; }
  .installment-radio .radio-dot { width: 14px; height: 14px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.7); flex-shrink: 0; background: transparent; }
  .installment-radio.selected .radio-dot { background: var(--widget-bg, #213F9A); border-color: var(--widget-bg, #213F9A); }

  /* E3 — investment summary (com course header integrado) */
  .investment-summary { background: rgba(255,255,255,0.08); border-radius: 10px; margin: 0.5rem 0; overflow: hidden; }
  .investment-summary.has-course-header .investment-summary-body { padding-top: 0.65rem; }
  .investment-summary-body { padding: 0.7rem 0.85rem 0.75rem; }
  .investment-course-header { background: rgba(255,255,255,0.18); padding: 0.4rem 0.85rem; font-weight: 600; font-size: 0.82rem; line-height: 1.25; text-align: center; }
  .investment-label { font-weight: 700; font-size: 0.82rem; margin-bottom: 0.25rem; }
  .investment-original { font-size: 0.78rem; opacity: 0.6; text-decoration: line-through; line-height: 1.2; }
  .investment-price { font-size: 1.45rem; font-weight: 800; margin: 0.1rem 0 0.2rem; line-height: 1.15; }
  .investment-tag { display: flex; align-items: center; gap: 0.45rem; font-size: 0.75rem; opacity: 0.9; margin-top: 0.2rem; line-height: 1.25; }
  .investment-tag-icon { width: 14px; height: 14px; flex-shrink: 0; opacity: 0.9; }

  /* E3 — coupon accordion */
  .coupon-accordion { border-top: 1px solid rgba(255,255,255,0.15); margin: 0.4rem 0 0.5rem; }
  .coupon-accordion-header { display: flex; justify-content: space-between; padding: 0.5rem 0; cursor: pointer; font-size: 0.85rem; }
  .coupon-chevron { opacity: 0.7; }
  .coupon-input-row { display: flex; align-items: center; gap: 0.5rem; padding-bottom: 0.4rem; }
  .coupon-input-row input { flex: 1; }
  .coupon-input-row input.coupon-input-invalid { border-color: #f87171 !important; color: #b91c1c; background: #fee2e2; }
  .coupon-badge-valid { font-size: 0.75rem; color: #4ade80; font-weight: 600; white-space: nowrap; }
  .coupon-badge-invalid { font-size: 0.75rem; color: #f87171; font-weight: 600; white-space: nowrap; }

  /* E3 — terms row */
  .terms-row { margin-top: 0.5rem; }
  .terms-check { display: flex; align-items: flex-start; gap: 0.5rem; font-size: 0.75rem; line-height: 1.35; cursor: pointer; }
  .terms-check input { margin-top: 2px; flex-shrink: 0; }
  .terms-link { color: inherit; }
`;

const E4_E5_CSS = `
  /* E4 — first installment banner */
  .payment-first-installment-banner { background: rgba(255,255,255,0.15); border-radius: 8px; padding: 0.55rem 1rem; text-align: center; margin-bottom: 0.85rem; font-size: 0.78rem; font-weight: 600; line-height: 1.35; }

  /* E4 — titles and hints */
  .payment-method-title { font-size: 1rem; font-weight: 700; text-align: center; margin: 0.75rem 0 0.5rem; }
  .payment-hint { font-size: 0.83rem; text-align: center; opacity: 0.85; margin-bottom: 0.75rem; }
  .payment-secure-tag { font-size: 0.78rem; text-align: center; opacity: 0.7; margin: 0.5rem 0; }
  .payment-email-note { font-size: 0.78rem; text-align: center; opacity: 0.7; margin-top: 0.75rem; }

  /* E4 — pix QR */
  .pix-qr-wrap { text-align: center; margin: 0.5rem 0; }
  .pix-qr-img { width: 150px; height: 150px; border-radius: 8px; }
  .pix-countdown { text-align: center; font-size: 1.1rem; font-weight: 700; margin: 0.3rem 0; }

  /* E4 — copy code block */
  .payment-code-block { background: rgba(0,0,0,0.2); border-radius: 6px; padding: 0.5rem 0.75rem; margin: 0.5rem 0; font-size: 0.72rem; word-break: break-all; font-family: monospace; }
  .payment-code-text { opacity: 0.9; }
  .btn-copy-code { display: block; width: 100%; padding: 0.7rem; background: var(--widget-accent, #f04e23); color: #fff; border: none; border-radius: 8px; font-weight: 700; font-size: 0.9rem; cursor: pointer; margin: 0.4rem 0; text-align: center; text-decoration: none; }
  .btn-copy-code.btn-outline { background: transparent; border: 2px solid rgba(255,255,255,0.5); }
  .btn-primary { display: block; width: 100%; padding: 0.85rem; background: var(--widget-accent, #f04e23); color: #fff; border: none; border-radius: 8px; font-weight: 700; font-size: 1rem; cursor: pointer; margin-top: 0.75rem; }
  .btn-change-payment-method { display: block; width: 100%; padding: 0.85rem 1.25rem; background: var(--widget-input-bg, #fff); color: #1e293b; border: none; border-radius: 8px; font-weight: 600; font-size: 0.95rem; cursor: pointer; margin-top: 0.5rem; text-align: center; }
  .btn-change-payment-method:hover { filter: brightness(0.97); }
  /* ponytail: ocultação temporária do botão "trocar forma de pagamento" (telas PIX/boleto/cartão e tela de erro). Reverter este bloco + rebuild do widget para reexibir. */
  .btn-change-payment-method, #btn-payment-change-plan { display: none !important; }

  /* E5 — success screen */
  .widget-success { padding: 1.5rem 1.25rem; }
  .success-screen { text-align: center; padding: 1rem 0; }
  .success-title { font-size: 1.3rem; font-weight: 900; margin-bottom: 1.25rem; line-height: 1.2; }
  .success-check-icon { margin: 0 auto 1rem; }
  .success-status-title { font-size: 1rem; font-weight: 700; margin-bottom: 1rem; }
  .success-body { font-size: 0.85rem; opacity: 0.88; margin-bottom: 0.5rem; line-height: 1.5; }
  .success-attention-box { background: rgba(255,255,255,0.12); border-radius: 8px; padding: 0.75rem 1rem; font-size: 0.78rem; opacity: 0.85; margin-top: 1.25rem; line-height: 1.5; }
`;

export const WIDGET_STYLES = shellCss.trim() + CARD_MOCK_CSS + PROGRESS_BAR_CSS + E1_CSS + E3_CSS + E4_E5_CSS;
